import {gsap} from "gsap/all";
import $ from "jquery";
import mqtt from "mqtt";
import {BaseGame} from '/js/app/base/BaseGame.js'
import FontFaceObserver from '../../libs/fontfaceobserver.js';
import {AudioPlaylist} from "./AudioPlaylist";
import {AudioPlayer} from "./Audio";
import {PillButton} from "../ui/PillButton";
import {LiquidGlass} from "../ui/LiquidGlass";
import * as phidget22 from 'phidget22';

// Constructor
export class BaseGameMars extends BaseGame {

  constructor(dataFile = 'data/data.json', appType) {

    // Super constructor
    super(dataFile);

    this.appType = appType;

    this.fonts = [
      // Inclusive Sans is a variable font (one file per style), so loading a
      // single weight pulls the whole range.
      new FontFaceObserver("Inclusive Sans", {weight: 400, style: "normal"}),
      new FontFaceObserver("Inclusive Sans", {weight: 400, style: "italic"}),
      new FontFaceObserver("HAL Matex", {weight: 500, style: "normal"}),
      new FontFaceObserver("HAL Matex", {weight: 700, style: "normal"}),
      new FontFaceObserver("GT Pressura Mono", {weight: 400, style: "normal"}),
    ];

    // Uptime
    this.upSince = Date.now();

    // Object class
    this.playlist = new AudioPlaylist();

    // Animation timeline
    this.currentTimeline = null;

    // Reset loop in case app can't connect to services or load assets
    this.restartAppOnErrorDelay = 1; // seconds

    // Wait for all
    // Load settings before everything else so settings are available to the other functions

    Promise.all([
      this.loadSettings(),
      this.loadData(),
      this.loadData("data/global.json"),
    ]).then(() => {
      // Preload audio files
      return this.preloadAudio();
    }).then(() => {
      // Preload fonts
      return this.preloadFonts();
    }).then(() => {
      // Ini nunjucks
      return this.initNunjucks();
    }).then(() => {
      // Replace strings {{ }}
      return this.renderNunjucks();
    }).then(() => {
      // Convert IMG to inline SVG
      return window.convertSVGToInline();
    }).then(() => {
      // Reused inline SVGs (icons included in more than one place) leave duplicate
      // ids behind, which breaks all copies but the first. Give the clashes unique
      // ids before anything references them.
      return window.uniquifyDuplicateSvgIds();
    }).then(() => {
      // Initiate inter-screen communication
      return (this.data.phidget?.enabled === true) ? this.initHardwareCommunication() : Promise.resolve();
    }).then(() => {
      // Real game init
      return this.initGame();
    }).then(() => {
      // init heartbeat
      return this.initHeartbeat(appType);
    }).catch((error) => {
      console.error("error", error)
      $("#main").show().append(`<div id='error'><p>Error: ${error.message}</p><br/><p>Restarting app in ${this.restartAppOnErrorDelay} seconds</p></div>`)

      setTimeout(() => {
        return this.restartApp();
      }, this.restartAppOnErrorDelay * 1000)
    });
  }

  initGame(defaultScreenId = 1) {
    this.initScreenSaver();
    this.goToScreen(defaultScreenId);

    this.initPillButtons();
    this.initLiquidGlass();
  }

  // Draw the asymmetric pill SVG for every pill-button in the DOM. Each instance
  // keeps a ResizeObserver, so they redraw on language switch / screen show.
  initPillButtons() {
    this.pillButtons = PillButton.initAll();
  }

  // Apply liquid-glass refraction to every [data-liquid-glass] element.
  // (Live tuning sliders live in LiquidGlassDebug.js; ?glassDebug.)
  initLiquidGlass() {
    this.liquidGlass = LiquidGlass.initAll();
  }

  // Only used for rover
  initMQTTScreenCommunication(mqttTopic) {
    const protocol = this.settings.mqtt.protocol;
    const host = this.settings.mqtt.host;
    const port = 8080;
    const client = mqtt.connect(`${protocol}://${host}:${port}`, {
      clientId: 'mqttjs_' + Math.random().toString(16).substr(2, 8),
    });

    client.on("close", () => {
      if (!this.mqttClient) console.warn("MQTT not yet connected (retrying...)");
    });

    client.on("connect", () => {
      console.log("MQTT connected. Subscribing to :", mqttTopic);
      client.subscribe(mqttTopic);
      this.mqttClient = client;
    });

    client.on("message", (messageTopic, message) => {
      const messageString = message.toString();
      this.onScreenBridgeMessage(messageString);
    });
  }

  // Can be overridden in child classes
  onScreenBridgeMessage(messageString) {
  }

  publishScreenBridgeMessage(topic, messageString) {
    if (!this.mqttClient) return;
    console.log("sendScreenBridgeMessage", topic, messageString);
    this.mqttClient.publish(topic, messageString);
  }

  initHeartbeat(appType) {
    if (window.inElectron()) {
      console.log("Init Heartbeat...");
      setInterval(() => {
        window.electronBridge.heartbeat()
      }, 1000)
    }
  }

  getPhidgetMap() {
    return this.data.phidget.map || [];
  }

  initHardwareCommunication() {
    console.log("Init Hardware Communication...");

    // The Promise always resolve
    return new Promise(async (resolve) => {

      // Connect to the Phidget server
      const conn = new phidget22.NetworkConnection(this.data.phidget.port, this.data.phidget.hostname);

      // Register for event before calling open
      conn.onDisconnect = () => {
        console.log("Disconnected from Phidget");
      };
      conn.onConnect = () => {
        console.log("Connected to Phidget");
      };

      // Actually connect (do not wait for connection to proceed)
      const connectWithRetry = async (conn) => {
        try {
          await conn.connect(1500);
        } catch (err) {
          // console.error(`Error during connect to Phidget Network server.`, err);
          await connectWithRetry(conn, name);
        }
      };
      connectWithRetry(conn);


      // Open each registred phidget
      for (let phidgetInput of this.getPhidgetMap()) {
        const phidgetName = `Phidget${phidgetInput.port}:${phidgetInput.channel} (${phidgetInput.action})`
        const digitalInput = new phidget22.DigitalInput();
        digitalInput.setHubPort(phidgetInput.port);
        digitalInput.setChannel(phidgetInput.channel);

        digitalInput.onAttach = () => {
          console.log(`${phidgetName} attached.`);
        };

        digitalInput.onDetach = () => {
          console.log(`${phidgetName} detached.`);
        };

        digitalInput.onStateChange = (state) => {
          const isPressed = (state.toString() === "true");

          // The console never touches the screen, so this stands in for the
          // document wide touch listener of the screensaver: any state change
          // of any input counts as activity, whatever the game does with it.
          this.resetTimer();

          this.onMessage(phidgetInput.action, isPressed);
          console.log(phidgetInput.action, isPressed);
        };

        // Actually open (do not wait for connection to proceed)
        const openWithRetry = async (input, name) => {
          try {
            await input.open(1500);
          } catch (err) {
            // console.error(`Error during connect to ${name}`, err);
            await openWithRetry(input, name);
          }
        };
        openWithRetry(digitalInput, phidgetName);
      }

      resolve();
    });
  }

  // To be overridden in child games
  onMessage(message) {
  }

  goToScreenSaver() {
    if ($("#still-there").hasClass("active")) {
      return false;
    }

    $("#still-there").addClass("active");
    this.onStillThereOpened();
    this.openStillThereModal();

    // The countdown running out is the player not answering, which is the
    // same as choosing to close the game, so it takes that exact path.
    this.stillThereTimeout = setTimeout(() => {
      this.screenSaverCloseGame();
    }, this.screensaverResetDelay * 1000); // screensaverResetDelay is in seconds
  }

  // Hooks for what a game has to hold while the modal is up. Overridden in
  // child games, they do nothing by default.
  onStillThereOpened() {
  }

  onStillThereClosed() {
  }

  openStillThereModal() {
    // Kill any previous open timeline before building a new one.
    this.killTimeline(this.stillThereTimeline);
    this.stillThereTimeline = gsap.timeline();

    this.stillThereTimeline.to($("#still-there"), {
      duration: 0.5,
      autoAlpha: 1
    });

    this.stillThereTimeline.fromTo($("#still-there .modal"), {
      autoAlpha: 0,
      y: "-10px",
    }, {
      duration: 0.5,
      autoAlpha: 1,
      y: 0,
    });

    // Countdown ring + number on the "keep playing" pill button
    const seconds = this.screensaverResetDelay;
    const $ring = $("#still-there .pill-button[data-icon='countdown'] .progress");
    const $count = $("#still-there .pill-button[data-icon='countdown'] .count");

    // Drive the dash from the circle's real circumference (r matches the SVG).
    const circumference = 2 * Math.PI * $ring.attr("r");

    $count.text(seconds);
    this.stillThereTimeline.fromTo($ring, {
      strokeDasharray: circumference,
      strokeDashoffset: 0, // 0 = full ring
    }, {
      duration: seconds,
      strokeDashoffset: -circumference, // use circumference to bring the ring to completely hidden
      ease: 'none',
    }, 0);

    const counter = {value: seconds};
    this.stillThereTimeline.to(counter, {
      duration: seconds,
      value: 0,
      ease: 'none',
      onUpdate: () => $count.text(Math.ceil(counter.value)),
    }, 0);
  }

  closeStillThereModal() {

    if (this.stillThereTimeout != false) {
      clearTimeout(this.stillThereTimeout);
      this.stillThereTimeout = false;
    }

    // Stop the countdown ring/number animation so it doesn't keep running.
    this.killTimeline(this.stillThereTimeline);
    this.stillThereTimeline = null;

    gsap.to($("#still-there"), 0.5, {
      autoAlpha: 0,
      onComplete: () => {
        $("#still-there").removeClass("active");
        this.onStillThereClosed();
      }
    });
  }

  screenSaverCloseGame() {
    this.reset(1);
    this.closeStillThereModal();
  }

  // Kill/reset timeline before building a new one
  killTimeline(timeline) {
    BaseGameMars.killTimeline(timeline);
  }

  static killTimeline(timeline) {
    if (timeline) {
      timeline.kill();
      timeline = null;
    }
  }

  // Reset values when we return to the homepage
  reset(screenId, resetCallback) {
    if (typeof screenId == "undefined") {
      screenId = 1;
    }

    // Return to idle screen.
    this.i18n.set(this.settings.default_language)

    this.goToScreen(screenId, () => {
      if (typeof resetCallback === "function") resetCallback();
    }, true, {allowSameScreenTransition: true});
  }
}
